/**
 * Admin API helpers.
 */
import axios from "axios";
import { API } from "./client";

/**
 * Get admin dashboard stats
 * @returns {Promise} Stats object with users, usage, system, and recent_activity
 */
export const getStats = async () => {
  const res = await axios.get(`${API}/admin/stats`);
  return res.data;
};

/**
 * List all users
 * @returns {Promise} Array of AdminUser objects
 */
export const listUsers = async () => {
  const res = await axios.get(`${API}/admin/users`);
  return res.data;
};

/**
 * Approve a user (set status to active)
 * @param {string} email - User email
 * @returns {Promise} Updated AdminUser object
 */
export const approveUser = async (email) => {
  const encodedEmail = encodeURIComponent(email);
  const res = await axios.post(`${API}/admin/users/${encodedEmail}/approve`);
  return res.data;
};

/**
 * Revoke a user (set status to revoked)
 * @param {string} email - User email
 * @returns {Promise} Updated AdminUser object
 */
export const revokeUser = async (email) => {
  const encodedEmail = encodeURIComponent(email);
  const res = await axios.post(`${API}/admin/users/${encodedEmail}/revoke`);
  return res.data;
};

/**
 * Get detailed user activity
 * @param {string} email - User email
 * @returns {Promise} User activity object with user, usage, and recent_actions
 */
export const getUserActivity = async (email) => {
  const encodedEmail = encodeURIComponent(email);
  const res = await axios.get(`${API}/admin/users/${encodedEmail}/activity`);
  return res.data;
};

/**
 * List all organizations
 * @returns {Promise} Array of organization objects [{id, name, status, created_at}]
 */
export const listOrganizations = async () => {
  const res = await axios.get(`${API}/admin/organizations`);
  return res.data;
};

/**
 * Create a new organization
 * @param {object} payload - {name, status?}
 * @returns {Promise} Created organization object
 */
export const createOrganization = async (payload) => {
  const res = await axios.post(`${API}/admin/organizations`, payload);
  return res.data;
};

/**
 * Update an existing organization. Setting status to 'inactive' cascades to
 * all users in that org (except super_admin/admin); setting it back to
 * 'active' restores only the users auto-deactivated by that cascade.
 * @param {string} orgId
 * @param {object} payload - {name?, status?}
 * @returns {Promise} Updated organization object
 */
export const updateOrganization = async (orgId, payload) => {
  const res = await axios.put(`${API}/admin/organizations/${orgId}`, payload);
  return res.data;
};

/**
 * Create a new user
 * @param {object} payload - User creation data
 * @returns {Promise} Created user object with temporary_password
 */
export const createUser = async (payload) => {
  const res = await axios.post(`${API}/admin/users`, payload);
  return res.data;
};

/**
 * Delete a user and all their associated data
 * @param {string} email - User email
 * @returns {Promise} Delete result with success, deleted_email, and counts
 */
export const deleteUser = async (email) => {
  const encodedEmail = encodeURIComponent(email);
  const res = await axios.delete(`${API}/admin/users/${encodedEmail}`);
  return res.data;
};

/**
 * Edit an existing user
 * @param {string} email - User email (cannot be changed)
 * @param {object} payload - User update data {name?, permissions?, organization_id?, role?}
 * @returns {Promise} Updated AdminUser object
 */
export const editUser = async (email, payload) => {
  const encodedEmail = encodeURIComponent(email);
  const res = await axios.patch(`${API}/admin/users/${encodedEmail}`, payload);
  return res.data;
};

/**
 * Admin-assisted password reset — generates a brand-new secure temporary
 * password for the target user and flips password_reset_required back to
 * true (forces them to set a permanent password on next login).
 * @param {string} email - User email
 * @returns {Promise} {email, temporary_password}
 */
export const resetUserPassword = async (email) => {
  const encodedEmail = encodeURIComponent(email);
  const res = await axios.post(`${API}/admin/users/${encodedEmail}/reset-password`);
  return res.data;
};

// ── Usage & Cost Tracking (F-014) ────────────────────────────────────────────

/**
 * Get paginated usage events
 * @param {object} params - Query params: user_email, model, agent_id, start_date, end_date, limit
 * @returns {Promise} {events: [], count: number}
 */
export const getUsageEvents = async (params = {}) => {
  const res = await axios.get(`${API}/admin/usage/events`, { params });
  return res.data;
};

/**
 * Get daily usage aggregation
 * @param {object} params - {user_email?, days?}
 * @returns {Promise} {daily: [], period_days: number}
 */
export const getUsageDaily = async (params = {}) => {
  const res = await axios.get(`${API}/admin/usage/daily`, { params });
  return res.data;
};

/**
 * Get usage breakdown by model
 * @param {object} params - {days?}
 * @returns {Promise} {by_model: [], period_days: number}
 */
export const getUsageByModel = async (params = {}) => {
  const res = await axios.get(`${API}/admin/usage/by-model`, { params });
  return res.data;
};

/**
 * Get usage breakdown by agent/feature
 * @param {object} params - {days?}
 * @returns {Promise} {by_agent: [], period_days: number}
 */
export const getUsageByAgent = async (params = {}) => {
  const res = await axios.get(`${API}/admin/usage/by-agent`, { params });
  return res.data;
};

/**
 * Get per-user usage breakdown
 * @param {object} params - {days?}
 * @returns {Promise} {by_user: [], period_days: number}
 */
export const getUsageByUser = async (params = {}) => {
  const res = await axios.get(`${API}/admin/usage/by-user`, { params });
  return res.data;
};

/**
 * Get cost summary for period
 * @param {object} params - {days?}
 * @returns {Promise} {total_cost, total_calls, total_input_tokens, total_output_tokens, total_cached_tokens, total_images, period_days}
 */
export const getUsageCostSummary = async (params = {}) => {
  const res = await axios.get(`${API}/admin/usage/cost-summary`, { params });
  return res.data;
};

// ── Invitations ─────────────────────────────────────────────────────────────

/**
 * Create an invitation to join the organization
 * @param {object} payload - { email, name?, permissions[] }
 * @returns {Promise} Invitation response with optional invite_url fallback
 */
export const createInvitation = async (payload) => {
  const res = await axios.post(`${API}/admin/invitations`, payload);
  return res.data;
};

/**
 * List invitations for the admin's organization
 * @returns {Promise} Array of invitation objects
 */
export const listInvitations = async () => {
  const res = await axios.get(`${API}/admin/invitations`);
  return res.data;
};

/**
 * Resend an invitation (regenerates token, resets expiry)
 * @param {string} id - Invitation ID
 * @returns {Promise} Updated invitation with optional invite_url fallback
 */
export const resendInvitation = async (id) => {
  const res = await axios.post(`${API}/admin/invitations/${id}/resend`);
  return res.data;
};

/**
 * Revoke a pending invitation
 * @param {string} id - Invitation ID
 * @returns {Promise} { success: true, id }
 */
export const revokeInvitation = async (id) => {
  const res = await axios.delete(`${API}/admin/invitations/${id}`);
  return res.data;
};

// ── Join Requests ───────────────────────────────────────────────────────────

/**
 * Submit a join request to an organization by name
 * @param {string} orgName - Organization name
 * @returns {Promise} Join request object
 */
export const submitJoinRequest = async (orgName) => {
  const res = await axios.post(`${API}/join-requests`, { organization_name: orgName });
  return res.data;
};

/**
 * Get the current user's join requests
 * @returns {Promise} Array of join request objects
 */
export const getMyJoinRequests = async () => {
  const res = await axios.get(`${API}/join-requests/mine`);
  return res.data;
};

/**
 * List pending join requests for the admin's organization
 * @returns {Promise} Array of join request objects
 */
export const listJoinRequests = async () => {
  const res = await axios.get(`${API}/admin/join-requests`);
  return res.data;
};

/**
 * Approve a join request
 * @param {string} id - Join request ID
 * @returns {Promise} Updated join request object
 */
export const approveJoinRequest = async (id) => {
  const res = await axios.post(`${API}/admin/join-requests/${id}/approve`);
  return res.data;
};

/**
 * Reject a join request
 * @param {string} id - Join request ID
 * @returns {Promise} Updated join request object
 */
export const rejectJoinRequest = async (id) => {
  const res = await axios.post(`${API}/admin/join-requests/${id}/reject`);
  return res.data;
};

// ── Audit Log ───────────────────────────────────────────────────────────────

/**
 * Get paginated audit log for the admin's organization
 * @param {object} params - { limit?, offset?, action? }
 * @returns {Promise} { items: [...], total: number }
 */
export const getAuditLog = async (params = {}) => {
  const res = await axios.get(`${API}/admin/audit`, { params });
  return res.data;
};
